// Stage 73.20dj — Finance/HR Advance Ledger (Dr/Cr view).
//
// Pick an employee → see the full accounting-style Dr/Cr ledger with
// running balance, per-advance breakdown, and export to CSV. Complements
// the existing AdvanceSettlements page (which is transaction-first) with
// an accountant-first view.
// Route: /finance/advance-ledger

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import {
  Download, User as UserIcon, RefreshCw, TrendingUp, TrendingDown,
  Search, Star, Clock, X, ChevronRight
} from 'lucide-react';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem
} from '../components/ui/command';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { AuthenticatedImage } from '../components/AuthenticatedImage';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function fmtINR(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const TXN_LABEL = {
  advance_disbursed: 'Advance Disbursed',
  claim_submitted: 'Claim Submitted',
  claim_approved: 'Claim Approved',
  claim_rejected: 'Claim Rejected',
  claim_adjustment: 'Claim Adjustment',
  salary_recovery: 'Salary Recovery',
  employee_refund: 'Employee Refund',
  company_reimbursement: 'Company Reimbursement',
  write_off: 'Write-off',
  carry_forward: 'Carry Forward',
  close: 'Closure',
};

// Helper to get employee initials for avatar
const getInitials = (name = '') => {
  return name
    .split(' ')
    .map(n => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const LedgerTimeline = ({ adv }) => {
  const steps = [
    { label: 'Advance Approved', done: true },
    { label: 'Finance Paid', done: adv.finance_paid || adv.finance_paid_amount > 0 },
    { label: 'Claim Submitted', done: adv.claim_submitted_amount > 0 || adv.claim_approved_amount > 0 },
    { label: 'Claim Approved', done: adv.claim_approved_amount > 0 },
    { label: 'Settlement Done', done: adv.settlement_status === 'Fully Settled' || adv.closed },
    { label: 'Closed', done: adv.closed }
  ];

  return (
    <div className="flex items-center gap-1.5 py-3 overflow-x-auto text-[10px] sm:text-xs">
      {steps.map((s, idx) => (
        <React.Fragment key={idx}>
          <div className="flex items-center gap-1 shrink-0">
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
              s.done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {s.done ? '✓' : idx + 1}
            </span>
            <span className={s.done ? 'text-emerald-700 font-semibold' : 'text-slate-500'}>
              {s.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <span className={`h-[2px] w-6 shrink-0 ${
              steps[idx + 1].done ? 'bg-emerald-400' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default function AdvanceLedger() {
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [employees, setEmployees] = useState([]);
  const [selEmpId, setSelEmpId] = useState('');
  const [ledger, setLedger] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [expandedAdvance, setExpandedAdvance] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [recentEmployees, setRecentEmployees] = useState(() => {
    const saved = localStorage.getItem('advance_ledger_recent_employees');
    return saved ? JSON.parse(saved) : [];
  });
  const [favoriteEmployees, setFavoriteEmployees] = useState(() => {
    const saved = localStorage.getItem('advance_ledger_favorite_employees');
    return saved ? JSON.parse(saved) : [];
  });
  const [filters, setFilters] = useState({
    department: '',
    payrollGroup: '',
    location: '',
    activeOnly: true
  });

  useEffect(() => {
    (async () => {
      try {
        const c = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(c.data || []);
        if ((c.data || []).length) setSelCompany(c.data[0].id);
      } catch { toast.error('Failed to load companies'); }
    })();
  }, []);

  useEffect(() => {
    if (!selCompany) return;
    (async () => {
      try {
        const e = await axios.get(`${API_URL}/employees?company_id=${selCompany}&limit=2000`, { withCredentials: true });
        setEmployees(e.data || []);
      } catch { setEmployees([]); }
    })();
  }, [selCompany]);

  // Persist recent/favorites to localStorage
  useEffect(() => {
    localStorage.setItem('advance_ledger_recent_employees', JSON.stringify(recentEmployees));
  }, [recentEmployees]);
  useEffect(() => {
    localStorage.setItem('advance_ledger_favorite_employees', JSON.stringify(favoriteEmployees));
  }, [favoriteEmployees]);

  // Get unique filters from employees (departments, payroll groups, locations)
  const uniqueFilters = useMemo(() => {
    const departments = [...new Set(employees.map(e => e.employment_details?.department || '').filter(Boolean))];
    const payrollGroups = [...new Set(employees.map(e => e.employment_details?.payroll_group || '').filter(Boolean))];
    const locations = [...new Set(employees.map(e => e.employment_details?.location || '').filter(Boolean))];
    return { departments, payrollGroups, locations };
  }, [employees]);

  // Filter and search employees
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      // Active only filter
      if (filters.activeOnly && emp.employment_details?.employment_status !== 'active') return false;
      // Quick filters
      if (filters.department && emp.employment_details?.department !== filters.department) return false;
      if (filters.payrollGroup && emp.employment_details?.payroll_group !== filters.payrollGroup) return false;
      if (filters.location && emp.employment_details?.location !== filters.location) return false;
      // Search query
      const q = searchQuery.toLowerCase();
      if (!q) return true;
      const fullName = (
        emp.personal_details?.full_name || 
        `${emp.personal_details?.first_name || ''} ${emp.personal_details?.last_name || ''}`.trim() || 
        ''
      ).toLowerCase();
      const empCode = (emp.employment_details?.employee_code || '').toLowerCase();
      const mobile = (emp.personal_details?.mobile || '').toLowerCase();
      const dept = (emp.employment_details?.department || '').toLowerCase();
      const email = (emp.personal_details?.email || emp.employment_details?.work_email || '').toLowerCase();
      return fullName.includes(q) || empCode.includes(q) || mobile.includes(q) || dept.includes(q) || email.includes(q);
    });
  }, [employees, searchQuery, filters]);

  // Organize employees: favorites first, then recent, then others
  const groupedEmployees = useMemo(() => {
    const favorites = filteredEmployees.filter(e => favoriteEmployees.includes(e.id));
    const recent = filteredEmployees.filter(e => recentEmployees.includes(e.id) && !favoriteEmployees.includes(e.id));
    const others = filteredEmployees.filter(e => !favoriteEmployees.includes(e.id) && !recentEmployees.includes(e.id));
    return { favorites, recent, others };
  }, [filteredEmployees, favoriteEmployees, recentEmployees]);

  const handleEmployeeSelect = async (empId) => {
    // Add to recent employees (keep only last 10, no duplicates)
    setRecentEmployees(prev => {
      const newRecent = [empId, ...prev.filter(id => id !== empId)];
      return newRecent.slice(0, 10);
    });
    setSelEmpId(empId);
    setSearchQuery('');
    setPopoverOpen(false);
  };

  const toggleFavorite = (empId, e) => {
    e.stopPropagation();
    setFavoriteEmployees(prev => {
      if (prev.includes(empId)) {
        return prev.filter(id => id !== empId);
      } else {
        return [...prev, empId];
      }
    });
  };

  const selectedEmployee = useMemo(() => {
    return employees.find(e => e.id === selEmpId);
  }, [employees, selEmpId]);

  useEffect(() => {
    if (!selEmpId) { setLedger(null); setSummary(null); return; }
    (async () => {
      setLoading(true);
      try {
        const [l, s] = await Promise.all([
          axios.get(`${API_URL}/advance-ledger/employee/${selEmpId}`, { withCredentials: true }),
          axios.get(`${API_URL}/advance-ledger/employee/${selEmpId}/summary`, { withCredentials: true }),
        ]);
        setLedger(l.data); setSummary(s.data);
      } catch (e) { toast.error('Failed to load ledger'); }
      setLoading(false);
    })();
  }, [selEmpId]);

  const exportCsv = async () => {
    if (!selEmpId) return;
    try {
      const res = await axios.get(
        `${API_URL}/advance-ledger/employee/${selEmpId}/export?fmt=csv`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `advance_ledger_${selEmpId.slice(-6)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Ledger exported');
    } catch { toast.error('Export failed'); }
  };

  // Helper to render an employee card in Command
  const EmployeeOptionCard = ({ employee, isFavorite }) => {
    const fullName = employee.personal_details?.full_name || 
      `${employee.personal_details?.first_name || ''} ${employee.personal_details?.last_name || ''}`.trim() || '—';
    const initials = getInitials(fullName);
    const status = employee.employment_details?.employment_status || '—';
    const statusColor = status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600';
    return (
      <div className="flex items-center gap-3 w-full py-2">
        <Avatar className="h-10 w-10 flex items-center justify-center overflow-hidden">
          <AuthenticatedImage 
            src={employee.personal_details?.photo_url} 
            alt={fullName} 
            className="h-full w-full object-cover rounded-full" 
            fallbackText={initials}
          />
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{fullName}</span>
            <Badge variant="outline" className="text-xs font-mono">{employee.employment_details?.employee_code || '—'}</Badge>
          </div>
          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-1">
            {employee.employment_details?.department && (
              <span className="flex items-center gap-1">
                <span className="text-slate-700 font-medium">Dept:</span> {employee.employment_details.department}
              </span>
            )}
            {employee.employment_details?.designation && (
              <span className="flex items-center gap-1">
                <span className="text-slate-700 font-medium">Designation:</span> {employee.employment_details.designation}
              </span>
            )}
            {employee.employment_details?.location && (
              <span className="flex items-center gap-1">
                <span className="text-slate-700 font-medium">Location:</span> {employee.employment_details.location}
              </span>
            )}
            {employee.employment_details?.payroll_group && (
              <span className="flex items-center gap-1">
                <span className="text-slate-700 font-medium">Payroll:</span> {employee.employment_details.payroll_group}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={statusColor}>{status}</Badge>
            {employee.personal_details?.mobile && (
              <span className="text-xs text-slate-400 font-mono">{employee.personal_details.mobile}</span>
            )}
          </div>
        </div>
        <button
          onClick={(e) => toggleFavorite(employee.id, e)}
          className="p-1 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0"
        >
          <Star className={`h-4 w-4 ${isFavorite ? 'fill-amber-400 text-amber-500' : 'text-slate-400'}`} />
        </button>
      </div>
    );
  };

  // Skeleton loader for summary
  const SummarySkeleton = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="border rounded-lg p-3 bg-slate-50">
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-6 w-28" />
        </div>
      ))}
    </div>
  );

  // Skeleton loader for table
  const TableSkeleton = () => (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-100">
        <Skeleton className="h-3 w-32" />
      </div>
      <div className="p-4">
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} className="h-4 w-full mb-2" />
        ))}
      </div>
    </div>
  );

  return (
    <MainLayout>
      <div className="p-6" data-testid="advance-ledger-page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Advance &amp; Claim Ledger</h1>
            <p className="text-sm text-slate-500 mt-1">
              Accounting-style Dr/Cr view of every advance disbursement, claim adjustment and settlement action per employee.
            </p>
          </div>
        </div>

        {/* Selector row + quick filters */}
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <select
              data-testid="advance-ledger-company"
              value={selCompany}
              onChange={(e) => { setSelCompany(e.target.value); setSelEmpId(''); }}
              className="border border-slate-300 rounded-md px-3 py-2 text-sm"
            >
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            {/* Employee search popover */}
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="secondary"
                  className="w-full max-w-md justify-start text-left font-normal border border-slate-200"
                >
                  {selectedEmployee ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6 flex items-center justify-center overflow-hidden">
                        <AuthenticatedImage
                          src={selectedEmployee.personal_details?.photo_url}
                          alt={
                            selectedEmployee.personal_details?.full_name || 
                            `${selectedEmployee.personal_details?.first_name || ''} ${selectedEmployee.personal_details?.last_name || ''}`.trim()
                          }
                          className="h-full w-full object-cover rounded-full"
                          fallbackText={getInitials(
                            selectedEmployee.personal_details?.full_name || 
                            `${selectedEmployee.personal_details?.first_name || ''} ${selectedEmployee.personal_details?.last_name || ''}`.trim()
                          )}
                        />
                      </Avatar>
                      <span className="font-medium">
                        {selectedEmployee.personal_details?.full_name || 
                          `${selectedEmployee.personal_details?.first_name || ''} ${selectedEmployee.personal_details?.last_name || ''}`.trim()}
                      </span>
                      <span className="text-xs text-slate-500 font-mono">
                        {selectedEmployee.employment_details?.employee_code}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Search className="h-4 w-4" />
                      <span>Search employee...</span>
                    </div>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[450px] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search by name, code, mobile, department, or email..."
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                  />
                  <CommandList className="max-h-[400px]">
                    <CommandEmpty>No employees found</CommandEmpty>
                    {groupedEmployees.favorites.length > 0 && (
                      <CommandGroup heading="Favorites">
                        {groupedEmployees.favorites.map(emp => (
                          <CommandItem
                            key={emp.id}
                            onSelect={() => handleEmployeeSelect(emp.id)}
                            value={emp.id}
                          >
                            <EmployeeOptionCard employee={emp} isFavorite={true} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    {groupedEmployees.recent.length > 0 && (
                      <CommandGroup heading="Recent">
                        {groupedEmployees.recent.map(emp => (
                          <CommandItem
                            key={emp.id}
                            onSelect={() => handleEmployeeSelect(emp.id)}
                            value={emp.id}
                          >
                            <EmployeeOptionCard employee={emp} isFavorite={favoriteEmployees.includes(emp.id)} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    {groupedEmployees.others.length > 0 && (
                      <CommandGroup heading="All Employees">
                        {groupedEmployees.others.slice(0, 50).map(emp => (
                          <CommandItem
                            key={emp.id}
                            onSelect={() => handleEmployeeSelect(emp.id)}
                            value={emp.id}
                          >
                            <EmployeeOptionCard employee={emp} isFavorite={favoriteEmployees.includes(emp.id)} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {selEmpId && (
              <div className="flex items-center gap-2">
                <button
                  data-testid="advance-ledger-refresh"
                  onClick={() => setSelEmpId(selEmpId)}
                  className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 rounded-md px-3 py-2 text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
                <button
                  data-testid="advance-ledger-export-csv"
                  onClick={exportCsv}
                  className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md px-3.5 py-2 text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
                <button
                  onClick={() => setSelEmpId('')}
                  className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 rounded-md px-3 py-2 text-sm"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Quick filters */}
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <span className="font-medium text-slate-600">Filters:</span>
            <select
              value={filters.department}
              onChange={e => setFilters(f => ({ ...f, department: e.target.value }))}
              className="border border-slate-200 rounded-md px-2 py-1"
            >
              <option value="">All Departments</option>
              {uniqueFilters.departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <select
              value={filters.payrollGroup}
              onChange={e => setFilters(f => ({ ...f, payrollGroup: e.target.value }))}
              className="border border-slate-200 rounded-md px-2 py-1"
            >
              <option value="">All Payroll Groups</option>
              {uniqueFilters.payrollGroups.map(pg => (
                <option key={pg} value={pg}>{pg}</option>
              ))}
            </select>
            <select
              value={filters.location}
              onChange={e => setFilters(f => ({ ...f, location: e.target.value }))}
              className="border border-slate-200 rounded-md px-2 py-1"
            >
              <option value="">All Locations</option>
              {uniqueFilters.locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.activeOnly}
                onChange={e => setFilters(f => ({ ...f, activeOnly: e.target.checked }))}
                className="rounded"
              />
              Active only
            </label>
          </div>
        </div>

        {!selEmpId && (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <UserIcon className="w-16 h-16 mx-auto mb-4 text-slate-200" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Select an employee to begin</h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Use the search box above to find an employee by name, employee code, mobile number, department, or email.
            </p>
          </div>
        )}

        {selEmpId && loading && (
          <div className="space-y-4">
            <div className="text-center py-4 text-slate-500 text-sm flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Loading Employee Ledger...
            </div>
            <SummarySkeleton />
            <TableSkeleton />
          </div>
        )}

        {selEmpId && !loading && summary && (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="advance-ledger-summary">
              <SummaryCard label="Total Advance" value={fmtINR(summary.total_advance)} tone="slate" />
              <SummaryCard label="Total Claim Approved" value={fmtINR(summary.total_claim_approved)} tone="emerald" />
              <SummaryCard label="Recovered / Refunded" value={fmtINR(summary.total_refund_by_employee + summary.total_recovered_via_salary + summary.total_written_off)} tone="sky" />
              <SummaryCard
                label="Net Balance"
                value={fmtINR(summary.net_balance)}
                tone={Math.abs(summary.net_balance) < 0.01 ? 'emerald' : summary.net_balance > 0 ? 'amber' : 'rose'}
                icon={Math.abs(summary.net_balance) < 0.01 ? null : summary.net_balance > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              />
            </div>

            <div className={`rounded-lg border p-3 mb-4 text-sm ${summary.status.tone === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : summary.status.tone === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-sky-50 border-sky-200 text-sky-800'
            }`} data-testid="advance-ledger-status">
              <strong>Status:</strong> {summary.status.label}
            </div>

            {/* Per-advance breakdown */}
            {summary.per_advance.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-4">
                <div className="px-4 py-2 border-b border-slate-100 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Advance-wise Tracking ({summary.advance_count})
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-1.5">Ref</th>
                      <th className="text-left px-3 py-1.5">Disbursed</th>
                      <th className="text-right px-3 py-1.5">Advance</th>
                      <th className="text-right px-3 py-1.5">Claim Approved</th>
                      <th className="text-right px-3 py-1.5">Recovery</th>
                      <th className="text-right px-3 py-1.5">Refund</th>
                      <th className="text-right px-3 py-1.5">Company Paid</th>
                      <th className="text-right px-3 py-1.5">Pending</th>
                      <th className="text-center px-3 py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.per_advance.map((a) => (
                      <tr key={a.advance_id} className="border-b border-slate-100" data-testid={`advance-row-${a.advance_id}`}>
                        <td className="px-3 py-1.5 font-mono">{a.loan_number}</td>
                        <td className="px-3 py-1.5 text-slate-600">{(a.disbursement_date || '').slice(0, 10)}</td>
                        <td className="px-3 py-1.5 text-right">{fmtINR(a.advance_amount)}</td>
                        <td className="px-3 py-1.5 text-right text-emerald-700">{fmtINR(a.claim_approved)}</td>
                        <td className="px-3 py-1.5 text-right text-sky-700">{fmtINR(a.salary_recovery)}</td>
                        <td className="px-3 py-1.5 text-right text-sky-700">{fmtINR(a.employee_refund)}</td>
                        <td className="px-3 py-1.5 text-right text-indigo-700">{fmtINR(a.company_reimbursement)}</td>
                        <td className={`px-3 py-1.5 text-right font-medium ${a.pending_balance > 0 ? 'text-amber-800' : a.pending_balance < 0 ? 'text-rose-800' : 'text-emerald-700'}`}>
                          {fmtINR(a.pending_balance)}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          {a.closed
                            ? <span className="text-xs bg-slate-700 text-white px-1.5 py-0.5 rounded-full">Closed</span>
                            : Math.abs(a.pending_balance) < 0.01
                              ? <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Settled</span>
                              : a.pending_balance > 0
                                ? <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">Open</span>
                                : <span className="text-xs bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded-full">Company owes</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
              {/* Dr / Cr transaction ledger */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Redesigned Parent-Child Transaction Ledger</h3>
            </div>
            
            {/* Search and Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="flex-1 min-w-[200px]">
                <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">Search Reference / Purpose</label>
                <input 
                  type="text"
                  placeholder="Search ADV / RBM ref..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full text-xs px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>
              
              <div className="w-[180px]">
                <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full text-xs px-2 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                  <option value="Fully Settled">Fully Settled</option>
                  <option value="Partially Settled">Partially Settled</option>
                  <option value="In Claim">In Claim</option>
                </select>
              </div>

              <div className="w-[140px]">
                <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">From Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full text-xs px-2 py-1.5 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>

              <div className="w-[140px]">
                <label className="text-[11px] font-bold uppercase text-slate-500 block mb-1">To Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full text-xs px-2 py-1.5 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
              </div>

              {(searchTerm || statusFilter !== 'ALL' || startDate || endDate) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('ALL');
                    setStartDate('');
                    setEndDate('');
                  }}
                  className="self-end px-3 py-2 text-xs bg-slate-200 text-slate-700 hover:bg-slate-300 rounded-md transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            {(() => {
              const filteredAdvances = (summary?.per_advance || []).filter(adv => {
                const matchesSearch = () => {
                  if (!searchTerm) return true;
                  const term = searchTerm.toLowerCase();
                  if (adv.finance_ref?.toLowerCase().includes(term)) return true;
                  if (adv.loan_number?.toLowerCase().includes(term)) return true;
                  if (adv.purpose?.toLowerCase().includes(term)) return true;
                  
                  const linkedClaims = (summary.claims || []).filter(c => c.advance_id === adv.advance_id);
                  if (linkedClaims.some(c => c.sheet_number?.toLowerCase().includes(term))) return true;
                  return false;
                };

                const matchesStatus = () => {
                  if (statusFilter === 'ALL') return true;
                  return adv.settlement_status === statusFilter || adv.payment_status === statusFilter || (adv.closed && statusFilter === 'Closed');
                };

                const matchesDate = () => {
                  if (!adv.disbursement_date) return true;
                  const disbDate = adv.disbursement_date.slice(0, 10);
                  if (startDate && disbDate < startDate) return false;
                  if (endDate && disbDate > endDate) return false;
                  return true;
                };

                return matchesSearch() && matchesStatus() && matchesDate();
              });

              if (filteredAdvances.length === 0) {
                return (
                  <div className="text-center py-16 bg-slate-50 rounded-lg">
                    <UserIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">No Ledger Records Found</h3>
                    <p className="text-slate-500">No advances match your search filters.</p>
                  </div>
                );
              }

              return filteredAdvances.map((adv) => {
                const isExpanded = expandedAdvance === adv.advance_id;
                const linkedClaims = (summary.claims || []).filter(c => c.advance_id === adv.advance_id);
                
                // Get all ledger rows for this advance
                const localRows = (ledger?.rows || [])
                  .filter(r => r.loan_id === adv.advance_id)
                  .sort((a, b) => new Date(a.txn_date) - new Date(b.txn_date));

                // Compute local running balance
                let runningBal = 0;
                const processedRows = localRows.map(r => {
                  runningBal += (r.debit || 0) - (r.credit || 0);
                  return { ...r, running_balance_local: runningBal };
                });

                return (
                  <div key={adv.advance_id} className="border border-slate-200 rounded-lg mb-4 overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
                    {/* Parent Header Row */}
                    <div 
                      className="flex flex-wrap items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors gap-4"
                      onClick={() => setExpandedAdvance(isExpanded ? null : adv.advance_id)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-slate-400 text-sm transition-transform duration-200">
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-slate-800 text-sm">{adv.finance_ref}</span>
                            <Badge variant="outline" className={`text-[10px] font-semibold ${
                              adv.closed ? 'bg-slate-100 text-slate-600' : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                              {adv.settlement_status}
                            </Badge>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            Purpose: <span className="font-medium text-slate-700">{adv.purpose}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-6 text-xs text-slate-600">
                        <div>
                          <div className="text-slate-400">Date</div>
                          <div className="font-medium mt-0.5">{(adv.disbursement_date || '').slice(0, 10)}</div>
                        </div>
                        <div>
                          <div className="text-slate-400">Approved Amount</div>
                          <div className="font-bold text-slate-800 mt-0.5">{fmtINR(adv.approved_amount)}</div>
                        </div>
                        <div>
                          <div className="text-slate-400">Outstanding</div>
                          <div className={`font-bold mt-0.5 ${adv.current_outstanding > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                            {fmtINR(adv.current_outstanding)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Child Transactions */}
                    {isExpanded && (
                      <div className="p-4 border-t border-slate-100 bg-white">
                        {/* Timeline flow */}
                        <div className="mb-6 pb-4 border-b border-slate-100">
                          <h4 className="text-[11px] font-bold uppercase text-slate-400 tracking-wider mb-2">Transaction Timeline</h4>
                          <LedgerTimeline adv={adv} />
                        </div>

                        {/* Details sections */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          {/* Finance Payment details */}
                          <div className="bg-slate-50 p-3 rounded-lg border border-slate-150">
                            <h5 className="text-xs font-bold text-slate-600 mb-2 border-b pb-1 border-slate-200">Finance Disbursement Payment</h5>
                            {adv.finance_paid_amount > 0 ? (
                              <div className="grid grid-cols-2 gap-y-2 text-xs">
                                <div>
                                  <span className="text-slate-400">Payment Date:</span>
                                  <p className="font-medium">{(adv.finance_payment_date || '').slice(0, 10)}</p>
                                </div>
                                <div>
                                  <span className="text-slate-400">Amount Paid:</span>
                                  <p className="font-bold text-slate-800">{fmtINR(adv.finance_paid_amount)}</p>
                                </div>
                                <div>
                                  <span className="text-slate-400">Payment Mode:</span>
                                  <p className="font-medium">{adv.payment_mode || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-400">UTR / Ref No:</span>
                                  <p className="font-mono text-slate-600 truncate" title={adv.finance_ref}>{adv.finance_ref || '-'}</p>
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-slate-500 py-4 text-center">No finance disbursement payments recorded.</div>
                            )}
                          </div>

                          {/* Linked Claims */}
                          <div className="bg-slate-50 p-3 rounded-lg border border-slate-150">
                            <h5 className="text-xs font-bold text-slate-600 mb-2 border-b pb-1 border-slate-200">Linked Expense Claims</h5>
                            {linkedClaims.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-slate-400 border-b border-slate-200 text-left">
                                      <th className="pb-1 font-semibold">Claim Ref</th>
                                      <th className="pb-1 text-right font-semibold">Submitted</th>
                                      <th className="pb-1 text-right font-semibold">Approved</th>
                                      <th className="pb-1 text-center font-semibold">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {linkedClaims.map((claim, cIdx) => (
                                      <tr key={cIdx} className="border-b border-slate-100 last:border-0 text-slate-700">
                                        <td className="py-1 font-mono font-medium">{claim.sheet_number || String(claim._id).slice(-6)}</td>
                                        <td className="py-1 text-right">{fmtINR(claim.total_amount)}</td>
                                        <td className="py-1 text-right text-emerald-600">
                                          {claim.status === 'approved' || claim.status === 'paid' || claim.status === 'settled' ? fmtINR(claim.total_amount) : '₹0.00'}
                                        </td>
                                        <td className="py-1 text-center">
                                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                            claim.status === 'approved' || claim.status === 'settled' ? 'bg-emerald-100 text-emerald-800' :
                                            claim.status === 'rejected' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                                          }`}>
                                            {claim.status}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-xs text-slate-500 py-4 text-center">No linked claims submitted for this advance.</div>
                            )}
                          </div>
                        </div>

                        {/* Child Sub-Ledger Table */}
                        <div>
                          <h4 className="text-[11px] font-bold uppercase text-slate-400 tracking-wider mb-2">Advance Ledger Details</h4>
                          {processedRows.length === 0 ? (
                            <div className="text-xs text-slate-500 text-center py-4">No ledger transaction entries recorded.</div>
                          ) : (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-slate-100 text-slate-600 border-b border-slate-200">
                                  <th className="text-left px-3 py-2 font-semibold">Date</th>
                                  <th className="text-left px-3 py-2 font-semibold">Ref No</th>
                                  <th className="text-left px-3 py-2 font-semibold">Type</th>
                                  <th className="text-left px-3 py-2 font-semibold">Particulars</th>
                                  <th className="text-right px-3 py-2 font-semibold">Debit</th>
                                  <th className="text-right px-3 py-2 font-semibold">Credit</th>
                                  <th className="text-right px-3 py-2 font-semibold">Running Balance</th>
                                  <th className="text-left px-3 py-2 font-semibold">Remarks</th>
                                </tr>
                              </thead>
                              <tbody>
                                {processedRows.map((r, k) => (
                                  <tr key={k} className="border-b border-slate-100 hover:bg-slate-50/50 text-slate-700">
                                    <td className="px-3 py-2">{(r.txn_date || '').slice(0, 10)}</td>
                                    <td className="px-3 py-2 font-mono">{r.ref_no}</td>
                                    <td className="px-3 py-2">{TXN_LABEL[r.txn_type] || r.txn_type}</td>
                                    <td className="px-3 py-2">
                                      {r.particulars}
                                      {r.txn_type === 'claim_adjustment' && r.claim_id && (
                                        <span className="text-[10px] text-slate-400 block">
                                          Against Claim: {(summary.claims || []).find(c => String(c._id) === r.claim_id)?.sheet_number || r.claim_id.slice(-6).toUpperCase()}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-right">{r.debit > 0 ? fmtINR(r.debit) : '-'}</td>
                                    <td className="px-3 py-2 text-right text-emerald-600">{r.credit > 0 ? fmtINR(r.credit) : '-'}</td>
                                    <td className={`px-3 py-2 text-right font-medium ${
                                      r.running_balance_local > 0 ? 'text-amber-700' : 'text-emerald-700'
                                    }`}>
                                      {fmtINR(r.running_balance_local)}
                                    </td>
                                    <td className="px-3 py-2 text-slate-500 max-w-xs truncate" title={r.remarks}>{r.remarks || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </>
        )}
      </div>
    </MainLayout>
  );
}

function SummaryCard({ label, value, tone = 'slate', icon = null }) {
  const cls = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    sky: 'bg-sky-50 border-sky-200 text-sky-800',
  }[tone];
  return (
    <div className={`border rounded-lg p-3 ${cls}`}>
      <div className="text-xs opacity-70 mb-1">{label}</div>
      <div className="text-lg font-bold flex items-center gap-1">
        {icon}{value}
      </div>
    </div>
  );
}
